import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button } from '../../../../ui';
import { faTrash, faRotateRight } from '@fortawesome/free-solid-svg-icons';

interface ResumeDraftDialogProps {
    open: boolean;
    onResume: () => void;
    onDiscard: () => void;
    draftInfo: {
        coffeeType: string;
        coffeeShop: string;
        step: number;
        hasImage: boolean;
        coffeeId?: string;
    } | null;
    draftAge: string;
}

const STEP_LABELS = ['Image', 'Location', 'Coffee', 'Rating'];

const ResumeDraftDialog: React.FC<ResumeDraftDialogProps> = ({
    open,
    onResume,
    onDiscard,
    draftInfo,
    draftAge
}) => {
    if (!draftInfo) return null;

    const stepLabel = STEP_LABELS[draftInfo.step - 1] || `Step ${draftInfo.step}`;
    const isPartialSubmission = !!draftInfo.coffeeId;

    return (
        <Dialog open={open} onOpenChange={() => {}}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <span className="text-2xl">☕</span>
                        Resume Previous Rating?
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                        {isPartialSubmission
                            ? "We found a partially submitted coffee entry. Would you like to continue uploading the image?"
                            : "You have an unfinished coffee rating. Would you like to continue where you left off?"
                        }
                    </p>

                    <div className="p-4 border border-ink/15 bg-muted/30 rounded-lg space-y-2">
                        {draftInfo.coffeeType && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">Coffee:</span>
                                <span className="text-sm text-muted-foreground capitalize">
                                    {draftInfo.coffeeType.replace(/-/g, ' ')}
                                </span>
                            </div>
                        )}
                        
                        {draftInfo.coffeeShop && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">Shop:</span>
                                <span className="text-sm text-muted-foreground">
                                    {draftInfo.coffeeShop}
                                </span>
                            </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">Progress:</span>
                            <span className="text-sm text-muted-foreground">
                                {isPartialSubmission ? 'Image upload pending' : `${stepLabel} step`}
                            </span>
                        </div>
                        
                        {draftInfo.hasImage && (
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-mint">✓ Image saved</span>
                            </div>
                        )}
                        
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>Saved {draftAge}</span>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            variant="outline"
                            onClick={onDiscard}
                            icon={faTrash}
                            iconPosition="left"
                            className="flex-1"
                        >
                            Start Fresh
                        </Button>
                        <Button
                            variant="default"
                            onClick={onResume}
                            icon={faRotateRight}
                            iconPosition="left"
                            className="flex-1"
                        >
                            Resume
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ResumeDraftDialog;
